import { MODE, PROD } from './env';

import type {
  AccountRole,
  Availability,
  BirthdayBonus,
  Business,
  BusinessCategory,
  Card,
  Challenge,
  ChallengeReward,
  CouponBenefits,
  CouponRecipient,
  CouponUsage,
  Integration,
  LevelBenefits,
  Product,
  PromotionGroupBenefits,
  ReferralBonus,
  StampFilter,
  StampType,
  StampValue,
} from '@rewardopl/types';

export const ACCOUNT_ROLE_IDS = {
  ADMIN: '4165dba5-35d6-4aab-8250-dbe71c0cfe64' as AccountRole['_id'],
  REWARDO_ADMIN: 'c53a8412-d4cb-4d33-8635-5f8f89e65c6f' as AccountRole['_id'],
  EMPLOYEE: '1723a7cf-2f91-4aef-843d-dd09459e064f' as AccountRole['_id'],
  MANAGER: '07bc0b01-8b96-476a-bd8b-309cc9b3b7bc' as AccountRole['_id'],
  OWNER: 'da461976-af10-4284-bbe4-9cb58e8a9e27' as AccountRole['_id'],
} as const satisfies Record<string, AccountRole['_id']>;

export const ALLOWED_IMAGE_MIMETYPES = [
  'image/gif',
  'image/heic',
  'image/jpg',
  'image/jpeg',
  'image/png',
  'image/webp',
] as const;

export const APP_IDS = {
  BUBBLEJOY: 'com.rewardoapp.bubblejoy',
  GRAMYLODY: 'com.rewardoapp.gramylody',
  LAJKONIK: 'com.rewardoapp.lajkonik',
  LODOWATO: 'com.rewardoapp.lodowato',
  REWARDO: 'com.rewardoapp.app',
  SALAD_STORY: 'com.rewardoapp.saladstory',
  SWEET_FACTORY_STORE: 'com.rewardoapp.sweetfactorystore',
} as const;

export const BIRTHDAY_BONUS_TYPES = {
  POINTS: 'points',
  REWARD: 'reward',
} as const satisfies Record<string, BirthdayBonus['type']>;

export const COUPON_AVAILABILITY_TYPES = {
  DATETIME_RANGE: 'datetime_range',
  HOURS: 'hours',
  UNLIMITED: 'unlimited',
} as const satisfies Record<string, Availability['type']>;

export const COUPON_BENEFIT_TYPES = {
  DISCOUNT_PERCENTAGE: 'discount_percentage',
  DISCOUNT_AMOUNT: 'discount_amount',
  STAMP_AMOUNT: 'stamp_amount',
  STAMP_MULTIPLY: 'stamp_multiply',
  FREEBIE: 'freebie',
} as const satisfies Record<string, CouponBenefits['type']>;

export const COUPON_RECIPIENT_TYPES = {
  ALL: 'all',
  CARD_SUBSCRIPTION: 'card_subscription',
  PROMOTION_GROUP: 'promotion_group',
} as const satisfies Record<string, CouponRecipient['type']>;

export const COUPON_USAGE_TYPES = {
  ONCE: 'once',
  ONCE_EVERY: 'once_every',
  UNLIMITED: 'unlimited',
} as const satisfies Record<string, CouponUsage['type']>;

export const BUSINESS_CATEGORIES = {
  FOOD_AND_BEVERAGES: 'food_and_beverages',
  BEAUTY_AND_WELLNESS: 'beauty_and_wellness',
  SERVICES: 'services',
  ENTERTAINMENT: 'entertainment',
  HOSPITALITY_AND_TOURISM: 'hospitality_and_tourism',
  HEALTH: 'health',
  TRANSPORTATION: 'transportation',
  FASHION: 'fashion',
  OTHER: 'other',
  ONLINE: 'online',
} as const satisfies Record<string, BusinessCategory>;

export const BUSINESS_IDS = {
  BUBBLEJOY: 'f9dea343-e690-444e-8696-7dea76caaa47' as Business['_id'],
  CAFE_PRZYGODA: 'e6a7532c-5eb7-4661-b4f3-e3c95b6696fd' as Business['_id'],
  GRAMYLODY: '08383ad9-2c21-4409-adbd-3b22b173184e' as Business['_id'],
  HOCKI_KLOCKI: 'e79bccb0-eabf-46cc-977a-70425329e83b' as Business['_id'],
  LAJKONIK: '0cb8aa7a-dc1e-428e-a50d-3a39453e3a93' as Business['_id'],
  LODOWATO: '417dd310-75b9-4e1c-b219-159ba7565e48' as Business['_id'],
  SALAD_STORY: '525fe9dd-34de-4d56-a700-fc0dcec23ab6' as Business['_id'],
  SWEET_FACTORY_STORE: '28d2eebc-66cf-4260-b563-1c26daf92c30' as Business['_id'],
} as const satisfies Record<string, Business['_id']>;

export const CARD_IDS = {
  BUBBLEJOY: '480c7e18-f5d5-426c-a750-fe0209f21638' as Card['_id'],
  GRAMYLODY: '9456c70d-accc-44bd-9ff9-060fbbdb5671' as Card['_id'],
  LAJKONIK: '1c7a0ac7-3948-4100-b918-7a298001f084' as Card['_id'],
  LODOWATO: 'b0f724b1-713f-42d4-982e-cca169d5dab9' as Card['_id'],
  SALAD_STORY: '46b8ca84-3483-473b-ba82-a69b3aa5fc07' as Card['_id'],
  SWEET_FACTORY_STORE: '18d411f9-b741-47d5-9a18-1eab8c427d93' as Card['_id'],
} as const satisfies Record<string, Card['_id']>;

export const CARD_APP_IDS = {
  [CARD_IDS.BUBBLEJOY]: APP_IDS.BUBBLEJOY,
  [CARD_IDS.GRAMYLODY]: APP_IDS.GRAMYLODY,
  [CARD_IDS.LAJKONIK]: APP_IDS.LAJKONIK,
  [CARD_IDS.LODOWATO]: APP_IDS.LODOWATO,
  [CARD_IDS.SALAD_STORY]: APP_IDS.SALAD_STORY,
  [CARD_IDS.SWEET_FACTORY_STORE]: APP_IDS.SWEET_FACTORY_STORE,
} as const satisfies Record<
  (typeof CARD_IDS)[keyof typeof CARD_IDS],
  (typeof APP_IDS)[keyof typeof APP_IDS]
>;

export const CARD_TYPES = {
  POINTS: 'points',
  STAMPS_STACKED: 'stamps_stacked',
} as const satisfies Record<string, Card['type']>;

export const CARD_TYPES_LABELS = {
  [CARD_TYPES.POINTS]: 'Points',
  [CARD_TYPES.STAMPS_STACKED]: 'Stamps',
} as const satisfies Record<(typeof CARD_TYPES)[keyof typeof CARD_TYPES], string>;

export const CHALLENGE_TYPES = {
  MEMORY_GAME: 'memory_game',
  MOST_POINTS: 'most_points',
  MOST_TRANSACTIONS: 'most_transactions',
  MOST_UNIQUE_DAYS: 'most_unique_days',
  QUIZ: 'quiz',
} as const satisfies Record<string, Challenge['type']>;

export const CHALLENGE_TYPES_LABELS = {
  [CHALLENGE_TYPES.MEMORY_GAME]: 'Game: memory',
  [CHALLENGE_TYPES.MOST_POINTS]: 'Most points',
  [CHALLENGE_TYPES.MOST_TRANSACTIONS]: 'Most transactions',
  [CHALLENGE_TYPES.MOST_UNIQUE_DAYS]: 'Most days with visits',
  [CHALLENGE_TYPES.QUIZ]: 'Quiz',
} as const satisfies Record<(typeof CHALLENGE_TYPES)[keyof typeof CHALLENGE_TYPES], string>;

export const CHALLENGE_REWARD_TYPES = {
  POINTS: 'points',
  REWARD: 'reward',
  NONE: 'none',
} as const satisfies Record<string, ChallengeReward['type']>;

export const COUNTRIES = {
  GERMANY: 'GERMANY',
  POLAND: 'POLAND',
  UNITED_KINGDOM: 'UK',
  UNITED_STATES: 'US',
} as const;

export const COUNTRY_CALLING_CODES = {
  GERMANY: '+49',
  POLAND: '+48',
  UNITED_KINGDOM: '+44',
  UNITED_STATES: '+1',
} as const satisfies Record<keyof typeof COUNTRIES, string>;

export const COUNTRY_CODES = {
  GERMANY: 'DE',
  POLAND: 'PL',
  UNITED_KINGDOM: 'UK',
  UNITED_STATES: 'US',
} as const satisfies Record<keyof typeof COUNTRIES, string>;

export const COUNTRY_NAMES = {
  GERMANY: 'Germany',
  POLAND: 'Poland',
  UNITED_KINGDOM: 'United Kingdom',
  UNITED_STATES: 'United States',
} as const satisfies Record<keyof typeof COUNTRIES, string>;

const isProduction = PROD;

export const GOOGLE_MAPS_API_KEY = isProduction
  ? 'AIzaSyD4Zbjw4ZtW25fD1dva_4_MZYmKo51nKlw'
  : 'AIzaSyADtUz4xTPae1kc8BZem8e0jUBgAHmMFZw';

export const GOPOS_URL = isProduction ? 'https://crm.gopos.io' : 'https://democrm.gopos.pl';

export const LEVEL_BENEFIT_TYPES = {
  DISCOUNT_PERCENTAGE: 'discount_percentage',
  DISCOUNT_AMOUNT: 'discount_amount',
  STAMP_AMOUNT: 'stamp_amount',
  STAMP_MULTIPLY: 'stamp_multiply',
  NONE: 'none',
} as const satisfies Record<string, LevelBenefits['type']>;

export const MOBILE_PHONE_PREFIXES = {
  GERMANY: ['15', '16', '17'],
  POLAND: ['45', '50', '51', '53', '57', '60', '66', '69', '72', '73', '78', '79', '88'],
  UNITED_KINGDOM: ['71', '73', '74', '75', '76', '77', '78', '79'],
  UNITED_STATES: [],
} as const satisfies Record<keyof typeof COUNTRIES, unknown>;

export const MONTHS = {
  JANUARY: 'JANUARY',
  FEBRUARY: 'FEBRUARY',
  MARCH: 'MARCH',
  APRIL: 'APRIL',
  MAY: 'MAY',
  JUNE: 'JUNE',
  JULY: 'JULY',
  AUGUST: 'AUGUST',
  SEPTEMBER: 'SEPTEMBER',
  OCTOBER: 'OCTOBER',
  NOVEMBER: 'NOVEMBER',
  DECEMBER: 'DECEMBER',
} as const;

export const MONTH_ABBRS = {
  JANUARY: 'Jan',
  FEBRUARY: 'Feb',
  MARCH: 'Mar',
  APRIL: 'Apr',
  MAY: 'May',
  JUNE: 'Jun',
  JULY: 'Jul',
  AUGUST: 'Aug',
  SEPTEMBER: 'Sep',
  OCTOBER: 'Oct',
  NOVEMBER: 'Nov',
  DECEMBER: 'Dec',
} as const satisfies Record<keyof typeof MONTHS, string>;

export const PHONE_FORMATS = {
  GERMANY: {
    LANDLINE: '## ######',
    MOBILE: '#### #######',
  },
  POLAND: {
    LANDLINE: '## ### ## ##',
    MOBILE: '### ### ###',
  },
  UNITED_KINGDOM: {
    LANDLINE: '## #### ####',
    MOBILE: '#### ### ###',
  },
  UNITED_STATES: {
    LANDLINE: '###-###-####',
    MOBILE: '###-###-####',
  },
} as const satisfies Record<
  keyof typeof COUNTRIES,
  {
    LANDLINE: string;
    MOBILE: string;
  }
>;

export const INTEGRATIONS = {
  DOTYKACKA: 'DOTYKACKA',
  FOODSOFT: 'FOODSOFT',
  GOPOS: 'GOPOS',
  IZZYREST: 'IZZYREST',
  SOFTMAR_MM: 'SOFTMAR_MM',
} as const;

export const INTEGRATION_NAMES = {
  DOTYKACKA: 'Dotykačka',
  FOODSOFT: 'Foodsoft',
  GOPOS: 'GoPOS',
  IZZYREST: 'izzyRest',
  SOFTMAR_MM: 'SOFTMAR-MM',
} as const satisfies Record<keyof typeof INTEGRATIONS, string>;

export const INTEGRATION_TYPES = {
  DOTYKACKA: 'dotykacka',
  FOODSOFT: 'foodsoft',
  GOPOS: 'gopos',
  IZZYREST: 'izzyrest',
  SOFTMAR_MM: 'softmar_mm',
} as const satisfies Record<keyof typeof INTEGRATIONS, Integration['type']>;

export const ITEM_TYPES = {
  PRODUCT: 'product',
  SERVICE: 'service',
} as const satisfies Record<string, Product['type']>;

export const ITEM_TYPES_LABELS = {
  [ITEM_TYPES.PRODUCT]: 'Product',
  [ITEM_TYPES.SERVICE]: 'Service',
} as const satisfies Record<(typeof ITEM_TYPES)[keyof typeof ITEM_TYPES], string>;

export const PROMOTION_GROUP_BENEFIT_TYPES = {
  DISCOUNT_PERCENTAGE: 'discount_percentage',
  DISCOUNT_AMOUNT: 'discount_amount',
  STAMP_AMOUNT: 'stamp_amount',
  STAMP_MULTIPLY: 'stamp_multiply',
  CUSTOM: 'custom',
  NONE: 'none',
} as const satisfies Record<string, PromotionGroupBenefits['type']>;

export const REFERRAL_BONUS_TYPES = {
  POINTS: 'points',
  REWARD: 'reward',
} as const satisfies Record<string, ReferralBonus['type']>;

const isDedicatedMode =
  MODE === 'lajkonik-production' ||
  MODE === 'lajkonik-test' ||
  MODE === 'saladstory-production' ||
  MODE === 'saladstory-test' ||
  MODE === 'sweetfactorystore-production' ||
  MODE === 'sweetfactorystore-test';

export const SHOULD_DISABLE_REGISTER_PAGE = isDedicatedMode;

export const STAMP_FILTER_TYPES = {
  ALL: 'all',
  EXCLUDE_ITEM_GROUP_NAMES: 'exclude_item_group_names',
  EXCLUDE_ITEM_NAMES: 'exclude_item_names',
  INCLUDE_ITEM_GROUP_NAMES: 'include_item_group_names',
  INCLUDE_ITEM_NAMES: 'include_item_names',
  POINTS: 'points',
} as const satisfies Record<string, StampFilter['type']>;

export const STAMP_TYPES = {
  DEFAULT: 'default_stamp',
  // Food and drink
  MUG: 'mug',
  COFFEE_BEAN: 'coffee_bean',
  COFFEE_CUP: 'coffee_cup',
  COFFEE_POT: 'coffee_pot',
  TEA_CUP: 'tea_cup',
  SODA: 'soda',
  BEER_PINT: 'beer_pint',
  COCKTAIL: 'cocktail',
  COCKTAIL_2: 'cocktail_2',
  WINE_GLASS: 'wine_glass',
  BURGER: 'burger',
  PIZZA: 'pizza',
  HOT_DOG: 'hot_dog',
  SANDWICH: 'sandwich',
  SALAD: 'salad',
  CAKE: 'cake',
  CUPCAKE: 'cupcake',
  PANCAKES: 'pancakes',
  ICE_CREAM_CONE: 'ice_cream_cone',
  ICE_CREAM_CUP: 'ice_cream_cup',
  POPSICLE: 'popsicle',
  SOFT_SERVE_CONE: 'soft_serve_cone',
  // Beauty and fashion
  NAIL_POLISH: 'nail_polish',
  HIGH_HEELS: 'high_heels',
  SHOES: 'shoes',
  // Services
  HAIR_DRYER: 'hair_dryer',
  COMB: 'comb',
  SCISSORS: 'scissors',
  ELECTRIC_RAZOR: 'electric_razor',
  SPONGE: 'sponge',
  BICYCLE: 'bicycle',
  CAR: 'car',
  WHEEL: 'wheel',
  HAMMER: 'hammer',
  DRILLER: 'driller',
  SEWING_MACHINE: 'sewing_machine',
  BRUSH: 'brush',
  KEY: 'key',
  PENCIL_AND_RULER: 'pencil_and_ruler',
  // Grocery
  SHOPPING_BAGS: 'shopping_bags',
  APPLE: 'apple',
  TOMATO: 'tomato',
  // Brands
  CAFE_PRZYGODA: 'cafe_przygoda',
  HOCKI_KLOCKI: 'hocki_klocki',
} as const satisfies Record<string, StampType>;

export const STAMP_TYPES_LABELS = {
  [STAMP_TYPES.DEFAULT]: 'Default',
  [STAMP_TYPES.COCKTAIL_2]: 'Cocktail (2)',
  [STAMP_TYPES.CAFE_PRZYGODA]: 'Cafe Przygoda',
  [STAMP_TYPES.HOCKI_KLOCKI]: 'Hocki Klocki',
  [STAMP_TYPES.ICE_CREAM_CONE]: 'Scoop ice cream cone',
} as const satisfies Partial<Record<(typeof STAMP_TYPES)[keyof typeof STAMP_TYPES], string>>;

export const STAMP_VALUES = {
  ABOVE: 'above',
  EACH: 'each',
  ITEM: 'item',
  VISIT: 'visit',
  CUSTOM: 'custom',
  UNDEFINED: 'undefined',
} as const satisfies Record<string, StampValue['type']>;

export const TIN_FORMATS = {
  POLAND: '###-###-##-##',
  UNITED_KINGDOM: '##### #####',
  UNITED_STATES: '##-#######',
} as const;

export const WEEKDAYS = {
  MONDAY: 'MONDAY',
  TUESDAY: 'TUESDAY',
  WEDNESDAY: 'WEDNESDAY',
  THURSDAY: 'THURSDAY',
  FRIDAY: 'FRIDAY',
  SATURDAY: 'SATURDAY',
  SUNDAY: 'SUNDAY',
} as const;

export const WEEKDAY_NAMES = {
  MONDAY: 'Monday',
  TUESDAY: 'Tuesday',
  WEDNESDAY: 'Wednesday',
  THURSDAY: 'Thursday',
  FRIDAY: 'Friday',
  SATURDAY: 'Saturday',
  SUNDAY: 'Sunday',
} as const satisfies Record<keyof typeof WEEKDAYS, string>;

export const WEEKDAY_ABBRS = {
  MONDAY: 'Mo',
  TUESDAY: 'Tu',
  WEDNESDAY: 'We',
  THURSDAY: 'Th',
  FRIDAY: 'Fr',
  SATURDAY: 'Sa',
  SUNDAY: 'Su',
} as const satisfies Record<keyof typeof WEEKDAYS, string>;
